import { sortCreatives } from "./sort-creatives";
import type {
	CampaignAdGroup,
	CampaignCardView,
	CampaignCreative,
	CampaignGeo,
	CampaignHourPoint,
	CampaignMetrics,
	CampaignPlacement,
	MetricArray,
	RawAdGroup,
	RawCampaignCardResponse,
	RawCampaignItem,
	RawCreative,
	RawGeoItem,
	RawHourlyPoint,
	RawPlacement,
} from "../types/campaign-card.type";

const CAMPAIGN_ID_PATTERN = /^\d{11}$/;

export function isValidCampaignCardId(id: string): boolean {
	return CAMPAIGN_ID_PATTERN.test(id);
}

function unwrapNum(values?: (number | null)[]): number {
	return values?.[0] ?? 0;
}

/** Preserves null — 0 means break-even ROI, not "no data". */
function unwrapNullableNum(values?: (number | null)[]): number | null {
	if (!values?.length) return null;
	const value = values[0];
	return value == null ? null : value;
}

function normalizeMetrics(raw: MetricArray): CampaignMetrics {
	const tcpa = unwrapNum(raw.tcpa);
	const cpaFact = unwrapNum(raw.cpa_fact);
	return {
		budget: raw.budget ? unwrapNum(raw.budget) : undefined,
		cost: unwrapNum(raw.cost),
		impr: unwrapNum(raw.impressions),
		inst: unwrapNum(raw.installs),
		reg: unwrapNum(raw.registrations),
		dep: unwrapNum(raw.deposits),
		dep_sum: unwrapNum(raw.deposit_sum),
		profit: unwrapNum(raw.profit),
		roas: unwrapNum(raw.roas),
		roi: unwrapNullableNum(raw.roi),
		tcpa: tcpa || undefined,
		cpa: cpaFact || undefined,
		cpi: unwrapNum(raw.cpi),
		epc: unwrapNum(raw.epc),
	};
}

function normalizePlacement(raw: RawPlacement): CampaignPlacement {
	return {
		name: raw.placement_name,
		metrics: normalizeMetrics(raw.metrics),
	};
}

function normalizeGeo(raw: RawGeoItem): CampaignGeo {
	return {
		country: raw.country,
		metrics: normalizeMetrics(raw.metrics),
		placements: (raw.placements ?? []).map(normalizePlacement),
	};
}

function normalizeCreative(raw: RawCreative): CampaignCreative {
	return {
		id: raw.id,
		adId: raw.ad_id,
		type: raw.creative_type,
		content: raw.creative_content,
		adStrength: raw.ad_strength,
		approvalStatus: raw.approval_status,
		metrics: { cost: 0, impr: 0 },
	};
}

function normalizeAdGroup(raw: RawAdGroup): CampaignAdGroup {
	return {
		id: raw.id,
		name: raw.name,
		status: raw.status,
		metrics: normalizeMetrics(raw.metrics),
		placements: (raw.placements ?? []).map(normalizePlacement),
		creatives: sortCreatives((raw.creatives ?? []).map(normalizeCreative)),
	};
}

function normalizeHourly(raw: RawHourlyPoint): CampaignHourPoint {
	return {
		hour: raw.hour,
		impressions: unwrapNum(raw.impressions),
		cost: unwrapNum(raw.cost),
		cpa_fact: unwrapNum(raw.cpa_fact),
		installs: unwrapNum(raw.installs),
		registrations: unwrapNum(raw.registrations),
		deposits: unwrapNum(raw.deposits),
		deposit_sum: unwrapNum(raw.deposit_sum),
	};
}

function normalizeCampaignItem(
	raw: RawCampaignItem,
	lastUpdatedAt?: string,
): CampaignCardView {
	return {
		id: raw.id,
		campaignId: raw.campaign_id,
		name: raw.name,
		status: raw.status,
		uam: raw.uam,
		appId: raw.app_id,
		isRent: raw.is_rent,
		isEditable: raw.is_editable ?? false,
		metrics: normalizeMetrics(raw.metrics),
		hourlyMetrics: (raw.hourly_metrics ?? []).map(normalizeHourly),
		geo: (raw.geo?.data ?? [])
			.map(normalizeGeo)
			.sort((a, b) => b.metrics.cost - a.metrics.cost),
		adgroups: (raw.adgroups?.data ?? []).map(normalizeAdGroup),
		lastUpdatedAt,
	};
}

export function pickCampaignFromResponse(
	payload: RawCampaignCardResponse,
	requestedId?: string,
): CampaignCardView | null {
	const root = payload[0]?.campaigns;
	const items = root?.data ?? [];
	const lastUpdatedAt = root?.last_updated_at;
	if (!items.length) return null;

	if (requestedId) {
		const match = items.find(
			(item) =>
				item.campaign_id === requestedId ||
				String(item.id) === requestedId,
		);
		if (match) return normalizeCampaignItem(match, lastUpdatedAt);
	}

	return normalizeCampaignItem(items[0], lastUpdatedAt);
}

export function normalizeCampaignCardResponse(
	payload: RawCampaignCardResponse,
	requestedId?: string,
): CampaignCardView | null {
	return pickCampaignFromResponse(payload, requestedId);
}
